import { useMutation } from '@tanstack/react-query'
import { Download, ScrollText, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { describeApiError } from '@/api/problemDetails'
import {
  Alert,
  Badge,
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  Skeleton,
  TablePagination,
  toast,
} from '@/components/ui'
import { useCurrentTenant } from '@/features/tenants/useCurrentTenant'
import { downloadBlob } from '@/lib/download'
import { translateEnumValue } from '@/lib/formatting'

import { exportAuditLogCsv } from './api'
import { useAuditLog } from './queries'
import { AUDIT_PAGE_SIZE, type AuditActorTypeFilter, type AuditLogEntry } from './types'

/** Teto de linhas da exportacao (espelha AuditLogQuery.MaxExportRows no backend). */
const MAX_EXPORT_ROWS = 10_000

const ACTOR_TYPES: readonly AuditActorTypeFilter[] = [
  'User',
  'System',
  'Agent',
  'PlatformAdministrator',
]

function useDateTimeFormatter(): (value: string) => string {
  const { i18n } = useTranslation()
  return (value: string) => {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
      return value
    }
    return new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date)
  }
}

interface AuditLabels {
  readonly actorTypeLabels: Readonly<Record<string, string>>
  readonly actionLabels: Readonly<Record<string, string>>
  readonly resourceLabels: Readonly<Record<string, string>>
}

/** Mapas de rotulos i18n (ator/acao/recurso) construidos explicitamente — sem returnObjects (typed-i18n seguro). */
function useAuditLabels(): AuditLabels {
  const { t } = useTranslation()

  return {
    actorTypeLabels: {
      User: t('audit.actorTypes.User'),
      System: t('audit.actorTypes.System'),
      Agent: t('audit.actorTypes.Agent'),
      PlatformAdministrator: t('audit.actorTypes.PlatformAdministrator'),
    },
    actionLabels: {
      'tenant.created': t('audit.actions.tenant.created'),
      'conversation.replied': t('audit.actions.conversation.replied'),
      'conversation.note_added': t('audit.actions.conversation.note_added'),
      'conversation.resolved': t('audit.actions.conversation.resolved'),
      'conversation.reopened': t('audit.actions.conversation.reopened'),
    },
    resourceLabels: {
      Tenant: t('audit.resources.Tenant'),
      Conversation: t('audit.resources.Conversation'),
    },
  }
}

/**
 * Auditoria (WP-16; design/12 s2): tabela (timestamp, ator, acao, recurso, correlacao) + drawer de detalhe
 * (metadados). So dado real persistido; distingue carregando de sem-dados; vocabulario neutro do produto.
 */
export function AuditPage() {
  const { t } = useTranslation()
  const { tenant } = useCurrentTenant()
  const [actorType, setActorType] = useState<AuditActorTypeFilter | null>(null)
  const [fromDate, setFromDate] = useState<string | null>(null)
  const [toDate, setToDate] = useState<string | null>(null)
  const [correlationId, setCorrelationId] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<AuditLogEntry | null>(null)

  // 'YYYY-MM-DD' compara lexicograficamente na ordem cronologica: intervalo invalido = inicio > fim.
  const invalidRange = fromDate !== null && toDate !== null && fromDate > toDate
  const correlationFilter = correlationId.trim() === '' ? null : correlationId.trim()
  const hasFilters =
    actorType !== null || fromDate !== null || toDate !== null || correlationFilter !== null

  // Intervalo invalido nao vai para a Api (evita consulta vazia sem sentido); mostramos o erro inline.
  const query = useAuditLog(tenant?.id, {
    actorType,
    fromDate: invalidRange ? null : fromDate,
    toDate: invalidRange ? null : toDate,
    correlationId: correlationFilter,
    page,
  })

  // Hook definido ANTES do early-return (regra dos hooks). O mutationFn so roda ao clicar (tenant ja existe);
  // `tenant?.id ?? ''` mantem o tipo seguro no caminho inalcancavel de tenant nulo.
  const exportMutation = useMutation({
    mutationFn: () =>
      exportAuditLogCsv(tenant?.id ?? '', {
        actorType,
        fromDate: invalidRange ? null : fromDate,
        toDate: invalidRange ? null : toDate,
        correlationId: correlationFilter,
      }),
    onSuccess: (result) => {
      downloadBlob(result.blob, 'auditoria.csv')
      if (result.truncated) {
        toast.warning(t('audit.export.truncated', { max: MAX_EXPORT_ROWS }))
      }
    },
    onError: (error) => {
      toast.error(describeApiError(error)[0] ?? t('audit.export.failed'))
    },
  })

  if (tenant === null) {
    return null
  }

  const clearFilters = (): void => {
    setActorType(null)
    setFromDate(null)
    setToDate(null)
    setCorrelationId('')
    setPage(1)
  }

  const actorTypeLabels: Readonly<Record<string, string>> = {
    User: t('audit.actorTypes.User'),
    System: t('audit.actorTypes.System'),
    Agent: t('audit.actorTypes.Agent'),
    PlatformAdministrator: t('audit.actorTypes.PlatformAdministrator'),
  }

  const data = query.data
  const total = data?.totalCount ?? 0
  const hasItems = data !== undefined && data.items.length > 0
  const from = total === 0 ? 0 : (page - 1) * AUDIT_PAGE_SIZE + 1
  const to = Math.min(page * AUDIT_PAGE_SIZE, total)
  const canPrev = page > 1
  const canNext = to < total

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground-primary">{t('audit.title')}</h1>
          <p className="text-sm text-foreground-muted">{t('audit.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="audit-actor-filter"
              className="text-xs font-medium text-foreground-muted"
            >
              {t('audit.filters.actorType')}
            </label>
            <select
              id="audit-actor-filter"
              value={actorType ?? ''}
              onChange={(event) => {
                const value = event.target.value
                setActorType(value === '' ? null : (value as AuditActorTypeFilter))
                setPage(1)
              }}
              className="h-9 rounded-md border border-border-default bg-background-elevated px-2 text-sm text-foreground-primary"
            >
              <option value="">{t('audit.filters.all')}</option>
              {ACTOR_TYPES.map((type) => (
                <option key={type} value={type}>
                  {actorTypeLabels[type]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="audit-from" className="text-xs font-medium text-foreground-muted">
              {t('audit.filters.from')}
            </label>
            <input
              id="audit-from"
              type="date"
              value={fromDate ?? ''}
              max={toDate ?? undefined}
              onChange={(event) => {
                const value = event.target.value
                setFromDate(value === '' ? null : value)
                setPage(1)
              }}
              className="h-9 rounded-md border border-border-default bg-background-elevated px-2 text-sm text-foreground-primary"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="audit-to" className="text-xs font-medium text-foreground-muted">
              {t('audit.filters.to')}
            </label>
            <input
              id="audit-to"
              type="date"
              value={toDate ?? ''}
              min={fromDate ?? undefined}
              onChange={(event) => {
                const value = event.target.value
                setToDate(value === '' ? null : value)
                setPage(1)
              }}
              className="h-9 rounded-md border border-border-default bg-background-elevated px-2 text-sm text-foreground-primary"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="audit-correlation"
              className="text-xs font-medium text-foreground-muted"
            >
              {t('audit.filters.correlationId')}
            </label>
            <input
              id="audit-correlation"
              type="search"
              value={correlationId}
              placeholder={t('audit.filters.correlationIdPlaceholder')}
              onChange={(event) => {
                setCorrelationId(event.target.value)
                setPage(1)
              }}
              className="h-9 w-52 rounded-md border border-border-default bg-background-elevated px-2 text-sm text-foreground-primary placeholder:text-foreground-muted"
            />
          </div>
          {hasFilters ? (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              {t('audit.filters.clear')}
            </Button>
          ) : null}
          <Button
            variant="secondary"
            size="sm"
            className="ml-auto"
            loading={exportMutation.isPending}
            disabled={!hasItems || invalidRange}
            onClick={() => {
              exportMutation.mutate()
            }}
          >
            <Download aria-hidden="true" className="h-4 w-4" />
            {t('audit.export.button')}
          </Button>
        </div>
        {invalidRange ? (
          <p role="alert" className="text-sm text-status-danger">
            {t('audit.filters.invalidRange')}
          </p>
        ) : null}
      </div>

      {query.isLoading ? (
        <div
          className="flex flex-col gap-px overflow-hidden rounded-lg border border-border-default"
          role="status"
          aria-busy="true"
          aria-label={t('audit.loading')}
        >
          {Array.from({ length: 8 }, (_, index) => (
            <div key={index} className="flex items-center gap-4 bg-background-elevated px-4 py-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-4 w-20" />
            </div>
          ))}
        </div>
      ) : query.isError ? (
        <Alert variant="danger" title={t('audit.loadFailed')}>
          {describeApiError(query.error)}
        </Alert>
      ) : !hasItems ? (
        <EmptyState
          icon={ScrollText}
          title={hasFilters ? t('audit.empty.filteredTitle') : t('audit.empty.title')}
          description={
            hasFilters ? t('audit.empty.filteredDescription') : t('audit.empty.description')
          }
          action={
            hasFilters ? (
              <Button variant="secondary" size="sm" onClick={clearFilters}>
                {t('audit.filters.clear')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <AuditTable entries={data.items} onSelect={setSelected} />
          <TablePagination
            summary={t('audit.pagination.summary', { from, to, total })}
            canPrev={canPrev}
            canNext={canNext}
            onPrev={() => {
              setPage((current) => Math.max(1, current - 1))
            }}
            onNext={() => {
              setPage((current) => current + 1)
            }}
            prevLabel={t('audit.pagination.prev')}
            nextLabel={t('audit.pagination.next')}
          />
        </>
      )}

      {selected !== null ? (
        <AuditDetailDrawer
          entry={selected}
          onClose={() => {
            setSelected(null)
          }}
        />
      ) : null}
    </div>
  )
}

function AuditTable({
  entries,
  onSelect,
}: {
  readonly entries: readonly AuditLogEntry[]
  readonly onSelect: (entry: AuditLogEntry) => void
}) {
  const { t } = useTranslation()
  const formatDateTime = useDateTimeFormatter()
  const { actorTypeLabels, actionLabels, resourceLabels } = useAuditLabels()

  const columns: readonly DataTableColumn<AuditLogEntry>[] = [
    {
      key: 'timestamp',
      header: t('audit.table.timestamp'),
      cell: (entry) => (
        <span className="text-foreground-secondary">{formatDateTime(entry.occurredAt)}</span>
      ),
    },
    {
      key: 'actor',
      header: t('audit.table.actor'),
      cell: (entry) => (
        <Badge variant="neutral">{translateEnumValue(actorTypeLabels, entry.actorType)}</Badge>
      ),
    },
    {
      key: 'action',
      header: t('audit.table.action'),
      cell: (entry) => (
        <span className="text-foreground-primary">
          {translateEnumValue(actionLabels, entry.action)}
        </span>
      ),
    },
    {
      key: 'resource',
      header: t('audit.table.resource'),
      cell: (entry) => (
        <span className="text-foreground-secondary">
          {translateEnumValue(resourceLabels, entry.resourceType)}
        </span>
      ),
    },
    {
      key: 'correlation',
      header: t('audit.table.correlation'),
      cell: (entry) => (
        <span className="font-mono text-xs text-foreground-muted">
          {entry.correlationId ?? '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('audit.table.viewDetail'),
      headerSrOnly: true,
      align: 'right',
      cell: (entry) => (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onSelect(entry)
          }}
          className="font-medium text-action-primary hover:underline"
        >
          {t('audit.table.viewDetail')}
        </button>
      ),
    },
  ]

  return (
    <DataTable
      columns={columns}
      rows={entries}
      rowKey={(entry) => entry.id}
      caption={t('audit.title')}
      onRowClick={onSelect}
    />
  )
}

function AuditDetailDrawer({
  entry,
  onClose,
}: {
  readonly entry: AuditLogEntry
  readonly onClose: () => void
}) {
  const { t } = useTranslation()
  const formatDateTime = useDateTimeFormatter()
  const { actorTypeLabels, actionLabels, resourceLabels } = useAuditLabels()

  const metadataEntries = entry.metadata === null ? [] : Object.entries(entry.metadata)

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <button
        type="button"
        aria-label={t('audit.detail.close')}
        className="absolute inset-0 bg-foreground-primary/50"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={t('audit.detail.title')}
        className="relative z-50 flex h-full w-full max-w-md flex-col gap-4 overflow-y-auto border-l border-border-default bg-background-elevated p-6 shadow-xl"
      >
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-lg font-semibold text-foreground-primary">
            {t('audit.detail.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('audit.detail.close')}
            className="text-foreground-muted hover:text-foreground-primary"
          >
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>

        <dl className="flex flex-col gap-3 text-sm">
          <DetailRow
            label={t('audit.detail.occurredAt')}
            value={formatDateTime(entry.occurredAt)}
          />
          <DetailRow
            label={t('audit.detail.actor')}
            value={`${translateEnumValue(actorTypeLabels, entry.actorType)}${
              entry.actorId === null ? '' : ` · ${entry.actorId}`
            }`}
          />
          <DetailRow
            label={t('audit.detail.action')}
            value={translateEnumValue(actionLabels, entry.action)}
          />
          <DetailRow
            label={t('audit.detail.resource')}
            value={`${translateEnumValue(resourceLabels, entry.resourceType)}${
              entry.resourceId === null ? '' : ` · ${entry.resourceId}`
            }`}
          />
          <DetailRow
            label={t('audit.detail.correlation')}
            value={entry.correlationId ?? '—'}
            mono
          />
        </dl>

        <div>
          <p className="mb-2 text-sm font-medium text-foreground-primary">
            {t('audit.detail.metadata')}
          </p>
          {metadataEntries.length === 0 ? (
            <p className="text-sm text-foreground-muted">{t('audit.detail.noMetadata')}</p>
          ) : (
            <dl className="flex flex-col gap-2 rounded-md border border-border-default bg-background-subtle p-3 text-sm">
              {metadataEntries.map(([key, value]) => (
                <div key={key} className="flex items-center justify-between gap-4">
                  <dt className="text-foreground-muted">{key}</dt>
                  <dd className="font-mono text-xs text-foreground-primary">
                    {typeof value === 'string' ? value : JSON.stringify(value)}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </aside>
    </div>
  )
}

function DetailRow({
  label,
  value,
  mono = false,
}: {
  readonly label: string
  readonly value: string
  readonly mono?: boolean
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-foreground-muted">{label}</dt>
      <dd
        className={
          mono
            ? 'text-right font-mono text-xs text-foreground-primary'
            : 'text-right font-medium text-foreground-primary'
        }
      >
        {value}
      </dd>
    </div>
  )
}
